/**
 * GlobalSearchModal — spec-46 W3 Cmd+K global search omnibar
 *
 * - MUI Dialog (fullscreen on mobile, ~640px centred on desktop)
 * - 300ms debounced query via useGlobalSearch hook
 * - 7 kind filter chips (multi-select, default = all)
 * - Results grouped by kind with AR/EN section headers
 * - Snippet safe renderer (only <b>…</b> allowed; everything else escaped)
 * - Recent searches from localStorage (key: jylaw.globalSearch.recent, max 8)
 * - Keyboard: ArrowUp/Down highlight + Enter navigate + Esc close
 * - RTL support: mirrors existing portal AR/EN pattern
 */

import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  TextField,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useGlobalSearch, ALL_SEARCH_KINDS } from '../api/search';
import type { SearchKind, SearchResult } from '../api/search';
import { useGlobalShortcut } from '../hooks/useGlobalShortcut';
import { useGlobalSearchContext } from '../contexts/GlobalSearchContext';
import { isRtl } from '../i18n/config';

// ---------------------------------------------------------------------------
// Recent-search persistence
// ---------------------------------------------------------------------------

const RECENT_KEY = 'jylaw.globalSearch.recent';
const RECENT_MAX = 8;

function loadRecent(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as string[];
  } catch { return []; }
}

function saveRecent(query: string): void {
  try {
    const existing = loadRecent().filter((q) => q !== query);
    const next = [query, ...existing].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch { /* storage unavailable */ }
}

// ---------------------------------------------------------------------------
// Safe snippet renderer — only <b>…</b> allowed; everything else escaped
// ---------------------------------------------------------------------------

function renderSnippet(raw: string | null): React.ReactNode {
  if (!raw) return null;
  // Split on <b> and </b> tags (case-insensitive).  Only these two tags are
  // trusted; anything else has already been HTML-escaped by the backend.
  const parts = raw.split(/(<b>|<\/b>)/gi);
  let bold = false;
  return (
    <>
      {parts.map((part, i) => {
        if (part.toLowerCase() === '<b>') { bold = true; return null; }
        if (part.toLowerCase() === '</b>') { bold = false; return null; }
        if (!part) return null;
        return bold ? (
          <Box key={i} component="mark" sx={{ bgcolor: 'rgba(11,37,69,0.12)', color: 'inherit', px: '1px', borderRadius: '2px', fontWeight: 700 }}>
            {part}
          </Box>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// isMac helper — determines ⌘K vs Ctrl+K hint in placeholder
// ---------------------------------------------------------------------------

function isMac(): boolean {
  return typeof navigator !== 'undefined' && /mac/i.test(navigator.platform);
}

// ---------------------------------------------------------------------------
// Kind label maps
// ---------------------------------------------------------------------------

const KIND_LABEL_EN: Record<SearchKind, string> = {
  company: 'Clients',
  matter: 'Matters',
  retainer_contract: 'Retainers',
  doc: 'Documents',
  email: 'Emails',
  proposal: 'Proposals',
  task: 'Tasks',
};

const KIND_LABEL_AR: Record<SearchKind, string> = {
  company: 'العملاء',
  matter: 'الملفات',
  retainer_contract: 'عقود الاحتفاظ',
  doc: 'المستندات',
  email: 'البريد',
  proposal: 'العروض',
  task: 'المهام',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const GlobalSearchModal: React.FC = () => {
  const { isOpen, close } = useGlobalSearchContext();
  const { t, i18n } = useTranslation('search');
  const rtl = isRtl(i18n.language);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Cmd+K / Ctrl+K toggles modal open (also handled at AppRoutes level via context)
  const { toggle } = useGlobalSearchContext();
  useGlobalShortcut('k', toggle);

  // Input state
  const [query, setQuery] = useState('');
  const [selectedKinds, setSelectedKinds] = useState<SearchKind[]>([...ALL_SEARCH_KINDS]);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const highlightedRef = useRef<HTMLDivElement | null>(null);

  // Data fetch
  const kindsParam = selectedKinds.length === ALL_SEARCH_KINDS.length ? undefined : selectedKinds;
  const { data, isFetching } = useGlobalSearch(query, kindsParam);

  // Flat result list (for keyboard nav)
  const flatResults: SearchResult[] = useMemo(() => {
    if (!data) return [];
    return data.results;
  }, [data]);

  // Recent searches
  const [recentSearches, setRecentSearches] = useState<string[]>(() => loadRecent());

  // Reset state on open
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setHighlightIndex(-1);
      setRecentSearches(loadRecent());
      // Autofocus — small delay for Dialog animation
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    highlightedRef.current?.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex]);

  const handleClose = useCallback(() => {
    close();
  }, [close]);

  const handleNavigate = useCallback(
    (result: SearchResult) => {
      if (query.trim()) saveRecent(query.trim());
      setRecentSearches(loadRecent());
      navigate(result.link);
      close();
    },
    [query, navigate, close],
  );

  const handleRecentClick = useCallback(
    (term: string) => {
      setQuery(term);
      inputRef.current?.focus();
    },
    [],
  );

  // Kind chip toggle
  const toggleKind = useCallback((kind: SearchKind) => {
    setHighlightIndex(-1);
    setSelectedKinds((prev) => {
      if (prev.length === ALL_SEARCH_KINDS.length) {
        // First click → select only this kind
        return [kind];
      }
      if (prev.includes(kind)) {
        const next = prev.filter((k) => k !== kind);
        // If deselecting last one, reset to all
        return next.length === 0 ? [...ALL_SEARCH_KINDS] : next;
      }
      const next = [...prev, kind];
      if (next.length === ALL_SEARCH_KINDS.length) return [...ALL_SEARCH_KINDS];
      return next;
    });
  }, []);

  // Keyboard handler inside modal
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIndex((i) => Math.min(i + 1, flatResults.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIndex((i) => Math.max(i - 1, -1));
        return;
      }
      if (e.key === 'Enter') {
        if (highlightIndex >= 0 && flatResults[highlightIndex]) {
          handleNavigate(flatResults[highlightIndex]);
        }
        return;
      }
    },
    [flatResults, highlightIndex, handleClose, handleNavigate],
  );

  // Group results by kind (preserving kind order)
  const grouped = useMemo(() => {
    if (!data) return [];
    const byKind = new Map<SearchKind, SearchResult[]>();
    for (const result of data.results) {
      const list = byKind.get(result.kind) ?? [];
      list.push(result);
      byKind.set(result.kind, list);
    }
    // Flat index for keyboard nav — build as we render
    let runningIndex = 0;
    return ALL_SEARCH_KINDS
      .filter((k) => (byKind.get(k)?.length ?? 0) > 0)
      .map((kind) => {
        const items = byKind.get(kind) ?? [];
        const startIndex = runningIndex;
        runningIndex += items.length;
        return { kind, items, startIndex };
      });
  }, [data]);

  const kindLabel = (kind: SearchKind) =>
    rtl ? KIND_LABEL_AR[kind] : KIND_LABEL_EN[kind];

  const shortcutHint = isMac() ? '⌘K' : 'Ctrl+K';
  const showEmpty = query.trim().length > 0 && !isFetching && data?.results.length === 0;
  const showRecent = query.trim().length === 0;

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : 640,
          maxWidth: '100%',
          maxHeight: isMobile ? '100%' : 600,
          borderRadius: isMobile ? 0 : 2,
          bgcolor: '#FFFFFF',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      sx={{
        '& .MuiBackdrop-root': { bgcolor: 'rgba(11,37,69,0.35)' },
      }}
      dir={rtl ? 'rtl' : 'ltr'}
    >
      <DialogContent
        sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        onKeyDown={handleKeyDown}
      >
        {/* ── Search input ── */}
        <Box sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: '1px solid #E3E7EC', flexShrink: 0 }}>
          <TextField
            inputRef={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setHighlightIndex(-1); }}
            placeholder={t('placeholder', { defaultValue: 'Search clients, matters, documents…' })}
            variant="outlined"
            size="small"
            fullWidth
            autoComplete="off"
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ color: '#4A5568', mr: 1, fontSize: 18 }} />
              ),
              endAdornment: isFetching ? (
                <CircularProgress size={14} sx={{ color: '#0B2545' }} />
              ) : (
                <Typography variant="caption" sx={{ color: '#9CA3AF', fontFamily: 'monospace', whiteSpace: 'nowrap', fontSize: '0.7rem' }}>
                  {shortcutHint}
                </Typography>
              ),
              sx: {
                bgcolor: '#FAFAF7',
                '& fieldset': { borderColor: '#E3E7EC' },
                '&:hover fieldset': { borderColor: '#C5CDD7' },
                '&.Mui-focused fieldset': { borderColor: '#0B2545' },
                fontSize: '0.9rem',
              },
            }}
          />
        </Box>

        {/* ── Kind filter chips ── */}
        <Box
          sx={{
            px: 2,
            py: 1,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            borderBottom: '1px solid #E3E7EC',
            flexShrink: 0,
          }}
        >
          {ALL_SEARCH_KINDS.map((kind) => {
            const allSelected = selectedKinds.length === ALL_SEARCH_KINDS.length;
            const active = allSelected || selectedKinds.includes(kind);
            return (
              <Chip
                key={kind}
                label={kindLabel(kind)}
                size="small"
                onClick={() => toggleKind(kind)}
                sx={{
                  fontSize: '0.72rem',
                  height: 24,
                  bgcolor: active ? 'rgba(11,37,69,0.08)' : 'transparent',
                  color: active ? '#0B2545' : '#9CA3AF',
                  border: '1px solid',
                  borderColor: active ? '#0B2545' : '#E3E7EC',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'rgba(11,37,69,0.12)' },
                  transition: 'all 0.15s',
                }}
              />
            );
          })}
        </Box>

        {/* ── Scrollable results / empty states ── */}
        <Box
          ref={listRef}
          sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0 }}
        >
          {/* Recent searches (when query empty) */}
          {showRecent && recentSearches.length > 0 && (
            <Box sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.68rem', fontWeight: 600 }}>
                {t('recent', { defaultValue: 'Recent searches' })}
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {recentSearches.map((term) => (
                  <Box
                    key={term}
                    onClick={() => handleRecentClick(term)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1,
                      py: 0.75,
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#F5F6F8' },
                    }}
                  >
                    <AccessTimeIcon sx={{ fontSize: 14, color: '#9CA3AF' }} />
                    <Typography variant="body2" sx={{ color: '#4A5568', fontSize: '0.84rem' }}>
                      {term}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Empty state: no recent and no query */}
          {showRecent && recentSearches.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <SearchIcon sx={{ fontSize: 36, color: '#E3E7EC', mb: 1 }} />
              <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: '0.84rem' }}>
                {t('placeholder', { defaultValue: 'Search clients, matters, documents…' })}
              </Typography>
            </Box>
          )}

          {/* Empty state: query with no results */}
          {showEmpty && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: '0.84rem' }}>
                {t('empty', { defaultValue: 'No results found' })}
              </Typography>
            </Box>
          )}

          {/* Results grouped by kind */}
          {grouped.map(({ kind, items, startIndex }) => (
            <Box key={kind}>
              {/* Section header */}
              <Box
                sx={{
                  px: 2,
                  py: 0.5,
                  bgcolor: '#F5F6F8',
                  borderBottom: '1px solid #E3E7EC',
                  borderTop: startIndex > 0 ? '1px solid #E3E7EC' : 'none',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#4A5568',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    fontSize: '0.67rem',
                    fontWeight: 700,
                  }}
                >
                  {kindLabel(kind)}
                </Typography>
              </Box>

              {/* Result rows */}
              {items.map((result, rowIdx) => {
                const flatIdx = startIndex + rowIdx;
                const isHighlighted = flatIdx === highlightIndex;
                return (
                  <Box
                    key={result.id}
                    ref={isHighlighted ? (el) => { highlightedRef.current = el as HTMLDivElement; } : undefined}
                    onClick={() => handleNavigate(result)}
                    sx={{
                      px: 2,
                      py: 1.25,
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 1.5,
                      cursor: 'pointer',
                      bgcolor: isHighlighted ? 'rgba(11,37,69,0.06)' : 'transparent',
                      borderBottom: '1px solid #F0F2F5',
                      '&:hover': { bgcolor: 'rgba(11,37,69,0.04)' },
                      transition: 'background-color 0.1s',
                    }}
                  >
                    {/* Left: title + subtitle + snippet */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          color: '#0E1116',
                          fontSize: '0.875rem',
                          lineHeight: 1.35,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {result.title}
                      </Typography>
                      {result.subtitle && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#4A5568',
                            fontSize: '0.78rem',
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {result.subtitle}
                        </Typography>
                      )}
                      {result.snippet && (
                        <Typography
                          variant="caption"
                          component="div"
                          sx={{
                            color: '#6B7280',
                            fontSize: '0.75rem',
                            mt: 0.25,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {renderSnippet(result.snippet)}
                        </Typography>
                      )}
                    </Box>

                    {/* Right: status pill */}
                    {result.status_pill && (
                      <Box sx={{ flexShrink: 0, mt: 0.25 }}>
                        <Chip
                          label={result.status_pill.label}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.67rem',
                            fontWeight: 600,
                            bgcolor: `${result.status_pill.color}20`,
                            color: result.status_pill.color,
                            border: '1px solid',
                            borderColor: `${result.status_pill.color}40`,
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          ))}

          {/* Loading state (first fetch) */}
          {isFetching && !data && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={20} sx={{ color: '#0B2545' }} />
            </Box>
          )}
        </Box>

        {/* ── Footer hint ── */}
        {flatResults.length > 0 && (
          <Divider />
        )}
        {flatResults.length > 0 && (
          <Box
            sx={{
              px: 2,
              py: 0.75,
              display: 'flex',
              gap: 2,
              bgcolor: '#FAFAF7',
              flexShrink: 0,
            }}
          >
            {[
              { key: '↑↓', label: rtl ? 'تنقل' : 'Navigate' },
              { key: '↵', label: rtl ? 'فتح' : 'Open' },
              { key: 'Esc', label: rtl ? 'إغلاق' : 'Close' },
            ].map(({ key, label }) => (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  component="kbd"
                  sx={{
                    display: 'inline-block',
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 0.5,
                    bgcolor: '#E3E7EC',
                    color: '#4A5568',
                    fontSize: '0.65rem',
                    fontFamily: 'monospace',
                    border: '1px solid #C5CDD7',
                    lineHeight: 1.4,
                  }}
                >
                  {key}
                </Box>
                <Typography variant="caption" sx={{ color: '#9CA3AF', fontSize: '0.68rem' }}>
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GlobalSearchModal;
